import { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Alert, Table, ButtonGroup } from 'react-bootstrap';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Sample } from '../../services/sample.service';
import { getAxiosOrErrorMessage, getErrorCode } from '../../utils/error.utils';
import {
  DEFAULT_USAGE_RECORD_QUANTITY,
  USAGE_ACTION,
  USAGE_ACTION_VALUES,
} from '../../constants/sample.constants';
import { canSampleAcceptUsageRecord } from '../../utils/sampleUsageRecords';
import { formatEntityDisplayName } from '../../utils/softBreakIdentifier';
import {
  formatSampleQuantityWithUnit,
  formatSampleUnitDisplay,
  SAMPLE_DEFAULT_UNIT,
} from '../../utils/sampleUnitLabels';
import { joinLabeledValue } from '../../utils/joinLabeledValue';
import TextContentSep from '../common/TextContentSep';
import BaseDateTimeField from './BaseDateTimeField';
import { AppModalCloseButton } from '../common/AppModalCloseButton';

export type BulkUsageRecordPayload = {
  sampleId: number;
  quantity: number;
  usageDate: string;
  notes?: string;
  action?: string;
};

type BulkUsageRecordFormData = {
  mode: 'bulk' | 'individual';
  action: (typeof USAGE_ACTION_VALUES)[number];
  bulkSettings: {
    usageDate: string;
    quantity: number;
    notes: string;
  };
  individualSettings: Array<{
    sampleId: number;
    usageDate: string;
    quantity: number;
    notes: string;
  }>;
};

interface BulkUsageRecordFormModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (records: BulkUsageRecordPayload[]) => Promise<void>;
  selectedSamples: Sample[];
  isLoading?: boolean;
}

function defaultUsageDate(): string {
  return format(new Date(), "yyyy-MM-dd'T'HH:mm");
}

export default function BulkUsageRecordFormModal({
  show,
  onHide,
  onSubmit,
  selectedSamples,
  isLoading = false,
}: BulkUsageRecordFormModalProps) {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'bulk' | 'individual'>('bulk');

  const availableSamples = useMemo(
    () => selectedSamples.filter((sample) => canSampleAcceptUsageRecord(sample)),
    [selectedSamples]
  );
  const unavailableSamples = useMemo(
    () => selectedSamples.filter((sample) => !canSampleAcceptUsageRecord(sample)),
    [selectedSamples]
  );

  /** 全対象が同じ単位のときだけ入力欄に単位を添える */
  const sharedBulkUnitLabel = useMemo(() => {
    if (availableSamples.length === 0) return null;
    const keys = new Set(
      availableSamples.map((s) => (s.unit?.trim() || SAMPLE_DEFAULT_UNIT).toLowerCase())
    );
    if (keys.size !== 1) return null;
    return formatSampleUnitDisplay(availableSamples[0]?.unit, t);
  }, [availableSamples, t]);

  const availableSampleIds = useMemo(
    () => availableSamples.map((s) => s.id).join(','),
    [availableSamples]
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<BulkUsageRecordFormData>({
    defaultValues: {
      mode: 'bulk',
      action: USAGE_ACTION.USE,
      bulkSettings: {
        usageDate: defaultUsageDate(),
        quantity: DEFAULT_USAGE_RECORD_QUANTITY,
        notes: '',
      },
      individualSettings: [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'individualSettings',
  });

  const bulkSettings = watch('bulkSettings');
  const individualSettings = watch('individualSettings');

  useEffect(() => {
    if (!show) return;
    const usageDate = defaultUsageDate();
    reset({
      mode: 'bulk',
      action: USAGE_ACTION.USE,
      bulkSettings: {
        usageDate,
        quantity: DEFAULT_USAGE_RECORD_QUANTITY,
        notes: '',
      },
      individualSettings: availableSamples.map((sample) => ({
        sampleId: sample.id,
        usageDate,
        quantity: DEFAULT_USAGE_RECORD_QUANTITY,
        notes: '',
      })),
    });
    setMode('bulk');
    setError('');
    // availableSampleIds で選択変化を検知（配列参照の無限ループ回避）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, availableSampleIds, reset]);

  const setFormMode = (next: 'bulk' | 'individual') => {
    setMode(next);
    setValue('mode', next);
  };

  const onSubmitForm = async (data: BulkUsageRecordFormData) => {
    try {
      setError('');
      if (availableSamples.length === 0) {
        setError(t('usage_record.no_available_samples'));
        return;
      }

      const actionValue = data.action || USAGE_ACTION.USE;

      if (data.mode === 'bulk') {
        const invalid = availableSamples.filter(
          (sample) =>
            sample.remainingQuantity !== null &&
            data.bulkSettings.quantity > sample.remainingQuantity
        );
        if (invalid.length > 0) {
          setError(
            t('usage_record.quantity_exceeds_remaining_bulk', {
              samples: invalid.map((s) => s.name).join(', '),
            })
          );
          return;
        }
        await onSubmit(
          availableSamples.map((sample) => ({
            sampleId: sample.id,
            quantity: data.bulkSettings.quantity,
            usageDate: data.bulkSettings.usageDate,
            notes: data.bulkSettings.notes || '',
            action: actionValue,
          }))
        );
        onHide();
        return;
      }

      const invalidNames: string[] = [];
      data.individualSettings.forEach((setting, index) => {
        const sample = availableSamples[index];
        if (
          sample &&
          sample.remainingQuantity !== null &&
          setting.quantity > sample.remainingQuantity
        ) {
          invalidNames.push(formatEntityDisplayName(sample.name, t));
        }
      });
      if (invalidNames.length > 0) {
        setError(
          t('usage_record.quantity_exceeds_remaining_bulk', {
            samples: invalidNames.join(', '),
          })
        );
        return;
      }

      const records: BulkUsageRecordPayload[] = [];
      for (let index = 0; index < data.individualSettings.length; index += 1) {
        const setting = data.individualSettings[index];
        const sample = availableSamples[index];
        if (!sample || !setting) continue;
        records.push({
          sampleId: sample.id,
          quantity: setting.quantity,
          usageDate: setting.usageDate,
          notes: setting.notes || '',
          action: actionValue,
        });
      }

      if (records.length === 0) {
        setError(t('usage_record.no_available_samples'));
        return;
      }

      await onSubmit(records);
      onHide();
    } catch (err: unknown) {
      const code = getErrorCode(err);
      const msg = getAxiosOrErrorMessage(err) || '';
      if (
        code === 'ECONNABORTED' ||
        msg.includes('timeout') ||
        msg.includes('api.error.request_timeout')
      ) {
        setError(t('api.error.request_timeout'));
      } else if (
        msg.startsWith('api.error.') ||
        msg.startsWith('sample.') ||
        msg.startsWith('usage_record.') ||
        msg.startsWith('common.')
      ) {
        setError(t(msg, { defaultValue: msg }));
      } else if (msg) {
        setError(msg);
      } else {
        setError(t('common.save_failed'));
      }
    }
  };

  return (
    <Modal
      dialogClassName="sample-system-dialog"
      show={show}
      onHide={onHide}
      size="lg"
      /* ネイティブ datetime ピッカーが Modal 外に出るためフォーカス閉じ込めを外す */
      enforceFocus={false}
    >
      <Modal.Header>
        <Modal.Title className="sample-detail-modal-title pe-2">
          <i className="fas fa-layer-group me-2 text-primary" aria-hidden="true" />
          {t('usage_record.bulk_modal_title', { count: availableSamples.length })}
        </Modal.Title>
        <AppModalCloseButton
          onClick={onHide}
          ariaLabel={t('common.close')}
          title={t('usage_record.modal_close_title', {
            defaultValue: '使用記録の追加を閉じる',
          })}
        />
      </Modal.Header>
      <Form id="bulk-usage-record-form" onSubmit={handleSubmit(onSubmitForm)}>
        <Modal.Body>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          {unavailableSamples.length > 0 ? (
            <Alert variant="warning" className="py-2 small mb-3">
              {t('usage_record.unavailable_samples_warning', {
                count: unavailableSamples.length,
              })}
            </Alert>
          ) : null}

          <ButtonGroup
            className="w-100 mb-3"
            size="sm"
            role="group"
            aria-label={t('usage_record.bulk_settings_title')}
          >
            <Button
              type="button"
              variant={mode === 'bulk' ? 'primary' : 'outline-primary'}
              onClick={() => setFormMode('bulk')}
              aria-pressed={mode === 'bulk'}
              title={t('usage_record.bulk_settings_title')}
            >
              {t('usage_record.bulk_settings')}
            </Button>
            <TextContentSep />
            <Button
              type="button"
              variant={mode === 'individual' ? 'primary' : 'outline-primary'}
              onClick={() => setFormMode('individual')}
              aria-pressed={mode === 'individual'}
              title={t('usage_record.individual_settings_title')}
            >
              {t('usage_record.individual_settings')}
            </Button>
          </ButtonGroup>
          <TextContentSep />

          <Form.Group className="mb-3">
            <Form.Label htmlFor="bulk-usage-action" title={t('usage_record.action_title')}>
              {t('usage_record.action')}
            </Form.Label>
            <Form.Select
              id="bulk-usage-action"
              aria-label={t('usage_record.action')}
              title={t('usage_record.action_title')}
              {...register('action')}
            >
              <option value={USAGE_ACTION.USE}>{t('usage_record.action_use')}</option>
              <option value={USAGE_ACTION.DISPOSE}>{t('usage_record.action_dispose')}</option>
              <option value={USAGE_ACTION.MOVE}>{t('usage_record.action_move')}</option>
              <option value={USAGE_ACTION.OTHER}>{t('usage_record.action_other')}</option>
            </Form.Select>
            <TextContentSep />
          </Form.Group>

          {mode === 'bulk' ? (
            <div>
              {availableSamples.length > 0 ? (
                <div className="mb-3">
                  <div className="text-muted small mb-1">{t('usage_record.selected_samples')}</div>
                  <div className="table-responsive">
                    <Table size="sm" bordered className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>{t('sample.sample_name')}</th>
                          <th className="text-end text-nowrap" style={{ width: '7rem' }}>
                            {t('sample.remaining_quantity')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableSamples.map((sample) => (
                          <tr key={sample.id}>
                            <td className="text-break">
                              {formatEntityDisplayName(sample.name, t)}
                            </td>
                            <td className="text-end text-nowrap">
                              {formatSampleQuantityWithUnit(
                                sample.remainingQuantity,
                                sample.unit,
                                t
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              ) : null}

              <Form.Group className="mb-3">
                <BaseDateTimeField
                  id="bulk-usage-date"
                  name="bulkSettings.usageDate"
                  label={t('usage_record.field_usage_date_label')}
                  required
                  value={bulkSettings?.usageDate ?? ''}
                  onChange={(v) =>
                    setValue('bulkSettings.usageDate', v, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                  isInvalid={!!errors.bulkSettings?.usageDate}
                  lang={i18n.language}
                />
                {errors.bulkSettings?.usageDate ? (
                  <div className="invalid-feedback d-block">
                    {errors.bulkSettings.usageDate.message}
                  </div>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label
                  htmlFor="bulk-usage-quantity"
                  title={t('usage_record.quantity')}
                >
                  {t('usage_record.field_quantity_label')}{' '}
                  <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    id="bulk-usage-quantity"
                    type="number"
                    min="0"
                    aria-label={t('usage_record.quantity')}
                    title={t('usage_record.field_quantity_title')}
                    aria-required="true"
                    {...register('bulkSettings.quantity', {
                      required: true,
                      valueAsNumber: true,
                      min: {
                        value: 0,
                        message: t('usage_record.quantity_min_error'),
                      },
                    })}
                    isInvalid={!!errors.bulkSettings?.quantity}
                    autoComplete="off"
                  />
                  {sharedBulkUnitLabel ? (
                    <span className="text-muted text-nowrap" aria-hidden="true">
                      {sharedBulkUnitLabel}
                    </span>
                  ) : null}
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.bulkSettings?.quantity?.message}
                </Form.Control.Feedback>
                {bulkSettings?.quantity === 0 ? (
                  <Form.Text className="text-muted" title={t('usage_record.quantity_note')}>
                    {t('usage_record.quantity_note')}
                  </Form.Text>
                ) : null}
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label htmlFor="bulk-usage-notes">
                  {t('usage_record.field_notes_label')}
                </Form.Label>
                <Form.Control
                  id="bulk-usage-notes"
                  as="textarea"
                  rows={2}
                  aria-label={t('usage_record.field_notes_label')}
                  {...register('bulkSettings.notes')}
                  placeholder={t('usage_record.notes_placeholder')}
                  autoComplete="off"
                />
              </Form.Group>
            </div>
          ) : (
            <div className="d-flex flex-column gap-2">
              {fields.map((field, index) => {
                const sample = availableSamples[index];
                if (!sample) return null;
                const sampleLabel = formatEntityDisplayName(sample.name, t);
                return (
                  <div key={field.id} className="border rounded p-2 bg-body-tertiary">
                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                      <div className="fw-semibold text-break min-w-0 small">
                        <span className="text-muted me-1">#{index + 1}</span>
                        <TextContentSep />
                        {sampleLabel}
                      </div>
                      <div
                        className="text-nowrap small text-muted"
                        title={joinLabeledValue(
                          t('usage_record.remaining_quantity_label'),
                          formatSampleQuantityWithUnit(
                            sample.remainingQuantity,
                            sample.unit,
                            t
                          )
                        )}
                      >
                        {joinLabeledValue(
                          t('sample.remaining_quantity_short'),
                          formatSampleQuantityWithUnit(
                            sample.remainingQuantity,
                            sample.unit,
                            t
                          )
                        )}
                      </div>
                    </div>
                    <Form.Group className="mb-2">
                      <BaseDateTimeField
                        id={`bulk-usage-date-${index}`}
                        name={`individualSettings.${index}.usageDate`}
                        label={t('usage_record.field_usage_date_label')}
                        required
                        value={individualSettings?.[index]?.usageDate ?? ''}
                        onChange={(v) =>
                          setValue(`individualSettings.${index}.usageDate`, v, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        isInvalid={!!errors.individualSettings?.[index]?.usageDate}
                        lang={i18n.language}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label
                        htmlFor={`bulk-usage-quantity-${index}`}
                        title={t('usage_record.quantity')}
                      >
                        {t('usage_record.field_quantity_label')}{' '}
                        <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id={`bulk-usage-quantity-${index}`}
                        type="number"
                        min="0"
                        max={sample.remainingQuantity ?? undefined}
                        aria-label={t('usage_record.quantity')}
                        title={t('usage_record.field_quantity_title')}
                        {...register(`individualSettings.${index}.quantity` as const, {
                          required: true,
                          valueAsNumber: true,
                          min: {
                            value: 0,
                            message: t('usage_record.quantity_min_error'),
                          },
                        })}
                        isInvalid={!!errors.individualSettings?.[index]?.quantity}
                        autoComplete="off"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.individualSettings?.[index]?.quantity?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label htmlFor={`bulk-usage-notes-${index}`}>
                        {t('usage_record.field_notes_label')}
                      </Form.Label>
                      <Form.Control
                        id={`bulk-usage-notes-${index}`}
                        as="textarea"
                        rows={2}
                        aria-label={t('usage_record.field_notes_label')}
                        {...register(`individualSettings.${index}.notes` as const)}
                        placeholder={t('usage_record.notes_placeholder')}
                        autoComplete="off"
                      />
                    </Form.Group>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={isLoading}
            aria-label={t('common.cancel')}
            title={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="bulk-usage-record-form"
            disabled={isLoading || availableSamples.length === 0}
            aria-label={
              isLoading
                ? t('common.saving')
                : t('usage_record.bulk_add', { count: availableSamples.length })
            }
            title={
              isLoading
                ? t('common.saving')
                : availableSamples.length === 0
                  ? t('usage_record.no_available_samples')
                  : t('usage_record.bulk_add', { count: availableSamples.length })
            }
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                {t('common.saving')}
              </>
            ) : (
              <>
                <i className="fas fa-save me-2" aria-hidden="true" />
                {t('usage_record.bulk_add', { count: availableSamples.length })}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
